import * as yup from "yup";
import {
  driveTypeSchema,
  hitchConfigSchema,
  positiveNumberSchema,
  ptoConfigSchema,
  scvOutputsSchema,
  steeringRangeSchema,
} from "../../schema";
import { getOutputGuid } from "portal/utils/ids";
import { OmitPick } from "common/utils/types";
import {
  optionalFieldToViewModel,
  OptionalViewModel,
  optionalViewModelToField,
} from "common/utils/viewModels";
import {
  persistTractorBoundaryViewModel,
  tractorArchetypeBoundaryViewModel,
} from "../boundaryModel";
import { Polygon2D } from "protos/geometry/geometry";
import {
  ScvOutputs,
  SteeringRange,
  TractorArchetype,
} from "protos/atk/autotractor";
import { TractorCalibration } from "protos/atk/calibration";
import { TractorDimensionsViewModel } from "../TractorDimensionsSubform";

type ExplicitFields = Pick<
  TractorArchetype,
  | "calibration"
  | "boundary"
  | "boundaryModel"
  | "steeringRange"
  | "wheelbaseM"
  | "wheelSpacingM"
>;

export const viewModelSchema = yup.object({
  make: yup
    .string()
    .typeError("utils.form.stringType")
    .required("utils.form.required"),
  model: yup
    .string()
    .typeError("utils.form.stringType")
    .required("utils.form.required"),
  series: yup.string().typeError("utils.form.stringType").optional(),

  scvOutputs: scvOutputsSchema.optional(),

  frontPto: ptoConfigSchema.optional(),
  rearPto: ptoConfigSchema.optional(),

  steeringRange: steeringRangeSchema.required("utils.form.required"),
  minTurningRadiusM: positiveNumberSchema.required("utils.form.required"),
  dimensions: yup.object({
    wheelbaseM: positiveNumberSchema.required("utils.form.required"),
    wheelSpacingM: positiveNumberSchema.required("utils.form.required"),
  }),

  driveType: driveTypeSchema.required("utils.form.required"),

  hitches: hitchConfigSchema.optional(),
});

export interface TractorArchetypeViewModel
  extends OmitPick<TractorArchetype, ExplicitFields> {
  steeringRange: SteeringRange;
  calibration: OptionalViewModel<TractorCalibration>;
  // this field exists so we can save back the existing polygon if and only if
  // the view model can't be baked into a replacement
  existingBoundaryPolygon: Polygon2D | undefined;
  dimensions: TractorDimensionsViewModel;
}

/**
 * this variable isn't used anywhere -- it just ensures that the schema and the
 * shape of the type it describes don't drift apart at typecheck time.
 */
const _: TractorArchetypeViewModel = tractorArchetypeToViewModel(
  undefined
) satisfies yup.InferType<typeof viewModelSchema>;

export function tractorArchetypeToViewModel(
  archetype: TractorArchetype | undefined
): TractorArchetypeViewModel {
  const baseArchetype = archetype ?? TractorArchetype.fromPartial({});
  const boundaryViewModel = tractorArchetypeBoundaryViewModel(baseArchetype);

  return {
    ...baseArchetype,
    steeringRange: baseArchetype.steeringRange ?? { min: -25, max: 25 },
    calibration: optionalFieldToViewModel(archetype?.calibration, {
      gpsPosXMm: 0,
      gpsPosYMm: 0,
      gpsHeadingOffsetDeg: 0,
      steeringCenterTrimDeg: 0,
      steeringLeftDeadbandMa: 0,
      steeringRightDeadbandMa: 0,
      steeringControlSignalMinMa: 0,
      steeringControlSignalMaxMa: 0,
      extraConfigs: [],
    }),
    existingBoundaryPolygon: baseArchetype.boundary,
    dimensions: {
      wheelbaseM: baseArchetype.wheelbaseM,
      wheelSpacingM: baseArchetype.wheelSpacingM,
      boundaryViewModel,
    },
  };
}

export function viewModelToTractorArchetype(
  viewModel: TractorArchetypeViewModel
): TractorArchetype {
  const scvOutputs: ScvOutputs = { scvOutput: [] };

  // Mutates the archetype in order to fix potential issues from
  // bad existing data.
  // Adds GUIDs to scvOutputs if they were missing
  if (viewModel.scvOutputs) {
    scvOutputs.scvOutput = viewModel.scvOutputs.scvOutput.map((o) => {
      const newOutput = { ...o };
      if (!o.id) {
        newOutput.id = getOutputGuid();
      }
      return o;
    });
  }

  return {
    ...viewModel,
    ...persistTractorBoundaryViewModel(
      viewModel.dimensions.boundaryViewModel,
      viewModel.existingBoundaryPolygon
    ),
    wheelbaseM: viewModel.dimensions.wheelbaseM,
    wheelSpacingM: viewModel.dimensions.wheelSpacingM,
    scvOutputs,
    calibration: optionalViewModelToField(viewModel.calibration),
  };
}

/**
 * A reusable hook which allows us to enable and disable editing for different
 * tractor archetype data model versions cleanly without having to manually
 * modify a bunch of individual forms.
 * @param _viewModel the tractor archetype view model (currently unused)
 * @returns true if editing the tractor archetype view model is supported,
 *   false otherwise
 */
export function editingSupported(
  _viewModel: TractorArchetypeViewModel
): boolean {
  return true;
}
